import React, { useState, useEffect, useCallback } from 'react'
import { activityAnalyticsAPI, platformAPI } from '../services/api'
import {
  BarChart3,
  GitCompare,
  TrendingUp,
  Building2,
  RefreshCw,
  ChevronDown,
  AlertCircle,
  Zap,
  ArrowDown,
  Clock,
} from 'lucide-react'

const CATEGORY_COLORS = {
  clients: 'bg-blue-500',
  whatsapp: 'bg-green-500',
  pipeline: 'bg-purple-500',
  products: 'bg-amber-500',
  questionnaires: 'bg-indigo-500',
  keywords: 'bg-teal-500',
  photos: 'bg-pink-500',
  branding: 'bg-orange-500',
  webhooks: 'bg-red-500',
  navigation: 'bg-gray-400',
  pipelines: 'bg-violet-500',
  users: 'bg-cyan-500',
  support: 'bg-lime-500',
}

const getCategoryColor = (category) =>
  CATEGORY_COLORS[category] || 'bg-gray-500'

const formatEventName = (name, context) => {
  const action = name
    .replace(/:/g, ' › ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

  if (!context) return action

  const ctx = context
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

  return `${action} — ${ctx}`
}

const DAYS_OPTIONS = [7, 14, 30, 60, 90]

// 'YYYY-MM-DD' → 'DD/MM/YYYY' without a timezone shift.
const brDay = (day) => (day ? day.split('-').reverse().join('/') : '')

const StatCard = ({ icon: Icon, label, value, subtitle }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <div className="flex items-center gap-3 mb-2">
      <div className="p-2 bg-gray-100 rounded-lg">
        <Icon className="h-5 w-5 text-gray-600" />
      </div>
      <span className="text-sm font-medium text-gray-500">{label}</span>
    </div>
    <div className="text-2xl font-bold text-gray-900">{value}</div>
    {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
  </div>
)

const HorizontalBar = ({ label, value, maxValue, color, subtitle }) => {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0
  return (
    <div className="group">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-700 truncate max-w-[60%]">
          {label}
        </span>
        <span className="text-sm font-semibold text-gray-900 tabular-nums">
          {value.toLocaleString('pt-BR')}
          {subtitle && (
            <span className="text-xs font-normal text-gray-500 ml-1">
              {subtitle}
            </span>
          )}
        </span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${Math.max(pct, 1)}%` }}
        />
      </div>
    </div>
  )
}

const TimelineBars = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="text-sm text-gray-500 py-8 text-center">Sem dados para este período</div>
  }
  const maxCount = Math.max(...data.map((d) => d.count), 1)
  return (
    <div className="flex items-end gap-1 h-40">
      {data.map((d) => {
        const pct = (d.count / maxCount) * 100
        return (
          <div
            key={d.date}
            className="flex-1 flex flex-col items-center justify-end group relative"
          >
            <div
              className="w-full bg-primary-500 rounded-t transition-all duration-300 min-h-[2px] hover:bg-primary-600"
              style={{ height: `${Math.max(pct, 2)}%` }}
            />
            <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
              {brDay(d.date)}: {d.count.toLocaleString('pt-BR')} eventos
            </div>
          </div>
        )
      })}
    </div>
  )
}

const formatHour = (h) => `${h}h`

const PeakHoursBars = ({ data, peakHour, quietestHour }) => {
  if (!data || data.length === 0) {
    return <div className="text-sm text-gray-500 py-8 text-center">Nenhum dado por hora disponível</div>
  }
  const maxCount = Math.max(...data.map((d) => d.event_count), 1)
  return (
    <div>
      <div className="flex items-end gap-0.5 h-40">
        {data.map((d) => {
          const pct = (d.event_count / maxCount) * 100
          const isPeak = d.hour === peakHour
          const isQuietest = d.hour === quietestHour
          const barColor = isPeak
            ? 'bg-emerald-500 hover:bg-emerald-600'
            : isQuietest
              ? 'bg-red-400 hover:bg-red-500'
              : 'bg-primary-400 hover:bg-primary-500'
          return (
            <div
              key={d.hour}
              className="flex-1 flex flex-col items-center justify-end group relative"
            >
              <div
                className={`w-full rounded-t transition-all duration-300 min-h-[2px] ${barColor}`}
                style={{ height: `${Math.max(pct, 2)}%` }}
              />
              <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                {formatHour(d.hour)}: {d.event_count.toLocaleString('pt-BR')} eventos, {d.unique_users} usuários
              </div>
            </div>
          )
        })}
      </div>
      <div className="flex justify-between text-xs text-gray-400 mt-2 px-1">
        <span>0h</span>
        <span>6h</span>
        <span>12h</span>
        <span>18h</span>
        <span>23h</span>
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-emerald-500 inline-block" />
          Pico: {peakHour != null ? formatHour(peakHour) : '—'}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-red-400 inline-block" />
          Mais tranquilo: {quietestHour != null ? formatHour(quietestHour) : '—'}
        </span>
      </div>
    </div>
  )
}

export const ActivityAnalyticsPage = () => {
  const [days, setDays] = useState(30)
  const [tenantId, setTenantId] = useState('')
  const [tenants, setTenants] = useState([])
  const [topFeatures, setTopFeatures] = useState([])
  const [timeline, setTimeline] = useState([])
  const [byTenant, setByTenant] = useState([])
  const [comparisonEvent, setComparisonEvent] = useState('')
  const [comparison, setComparison] = useState([])
  const [summary, setSummary] = useState({ total_events: 0, unique_users: 0 })
  const [peakHours, setPeakHours] = useState({ data: [], peak_hour: null, quietest_hour: null })
  const [loading, setLoading] = useState(true)
  const [aggregating, setAggregating] = useState(false)
  const [error, setError] = useState(null)

  const loadTenants = useCallback(async () => {
    try {
      const res = await platformAPI.getTenants({ limit: 200 })
      const list = res.data?.data || res.data?.tenants || []
      setTenants(Array.isArray(list) ? list : [])
    } catch {
      // ignore
    }
  }, [])

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = { days }
      if (tenantId) params.tenant_id = tenantId

      const [topRes, tlRes, btRes, phRes] = await Promise.allSettled([
        activityAnalyticsAPI.getTopFeatures({ ...params, limit: 20 }),
        activityAnalyticsAPI.getTimeline(params),
        activityAnalyticsAPI.getByTenant({ days, limit: 15 }),
        activityAnalyticsAPI.getPeakHours(params),
      ])

      if (topRes.status === 'fulfilled') {
        setTopFeatures(topRes.value.data?.data || [])
        setSummary(topRes.value.data?.summary || { total_events: 0, unique_users: 0 })
      }
      if (tlRes.status === 'fulfilled') setTimeline(tlRes.value.data?.data || [])
      if (btRes.status === 'fulfilled') setByTenant(btRes.value.data?.data || [])
      if (phRes.status === 'fulfilled') {
        const ph = phRes.value.data || {}
        setPeakHours({ data: ph.data || [], peak_hour: ph.peak_hour, quietest_hour: ph.quietest_hour })
      }
    } catch (err) {
      setError('Falha ao carregar os dados de análise')
    } finally {
      setLoading(false)
    }
  }, [days, tenantId])

  const loadComparison = useCallback(async () => {
    if (!comparisonEvent) {
      setComparison([])
      return
    }
    try {
      const params = { event_name: comparisonEvent, days }
      if (tenantId) params.tenant_id = tenantId
      const res = await activityAnalyticsAPI.getComparison(params)
      setComparison(res.data?.data || [])
    } catch {
      setComparison([])
    }
  }, [comparisonEvent, days, tenantId])

  const handleRunAggregation = async () => {
    setAggregating(true)
    try {
      await activityAnalyticsAPI.runAggregation()
      await loadData()
    } catch {
      // ignore
    } finally {
      setAggregating(false)
    }
  }

  useEffect(() => {
    loadTenants()
  }, [loadTenants])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    loadComparison()
  }, [loadComparison])

  useEffect(() => {
    if (topFeatures.length > 0 && !comparisonEvent) {
      const whatsappEvent = topFeatures.find((f) =>
        f.event_name.startsWith('whatsapp:')
      )
      if (whatsappEvent) setComparisonEvent(whatsappEvent.event_name)
    }
  }, [topFeatures, comparisonEvent])

  const topFeature = topFeatures[0]
  const leastUsed = topFeatures.length > 1 ? topFeatures[topFeatures.length - 1] : null
  const topFeaturesMax = topFeatures[0]?.total_count || 1

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary-600" />
            Análise de atividade
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Acompanhamento do uso de recursos em todos os estúdios
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Filtrar por estúdio"
          >
            <option value="">Todos os estúdios</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name || t.slug}
              </option>
            ))}
          </select>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Selecionar período"
          >
            {DAYS_OPTIONS.map((d) => (
              <option key={d} value={d}>
                Últimos {d} dias
              </option>
            ))}
          </select>
          <button
            onClick={handleRunAggregation}
            disabled={aggregating}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            aria-label="Executar agregação"
          >
            <RefreshCw className={`h-4 w-4 ${aggregating ? 'animate-spin' : ''}`} />
            Agregar
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={BarChart3}
              label="Total de eventos"
              value={summary.total_events.toLocaleString('pt-BR')}
              subtitle={`Últimos ${days} dias`}
            />
            <StatCard
              icon={TrendingUp}
              label="Usuários únicos"
              value={summary.unique_users.toLocaleString('pt-BR')}
              subtitle="Usuários distintos no período"
            />
            <StatCard
              icon={Zap}
              label="Mais usado"
              value={topFeature ? formatEventName(topFeature.event_name, topFeature.event_context) : '—'}
              subtitle={topFeature ? `${topFeature.total_count.toLocaleString('pt-BR')} usos` : ''}
            />
            <StatCard
              icon={ArrowDown}
              label="Menos usado"
              value={leastUsed ? formatEventName(leastUsed.event_name, leastUsed.event_context) : '—'}
              subtitle={leastUsed ? `${leastUsed.total_count.toLocaleString('pt-BR')} usos` : ''}
            />
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-gray-500" />
              Atividade de eventos ao longo do tempo
            </h2>
            <TimelineBars data={timeline} />
            {timeline.length > 0 && (
              <div className="flex justify-between text-xs text-gray-400 mt-2 px-1">
                <span>{brDay(timeline[0]?.date)}</span>
                <span>{brDay(timeline[timeline.length - 1]?.date)}</span>
              </div>
            )}
          </div>

          {/* Peak Usage Hours */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-500" />
              Horários de pico de uso
              <span className="text-xs font-normal text-gray-400">(UTC)</span>
            </h2>
            <PeakHoursBars
              data={peakHours.data}
              peakHour={peakHours.peak_hour}
              quietestHour={peakHours.quietest_hour}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Features */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-gray-500" />
                Recursos mais usados
              </h2>
              {topFeatures.length === 0 ? (
                <div className="text-sm text-gray-500 py-8 text-center">
                  Nenhum evento registrado ainda. Os dados aparecerão após a primeira agregação.
                </div>
              ) : (
                <div className="space-y-4">
                  {topFeatures.map((f) => (
                    <HorizontalBar
                      key={`${f.event_name}-${f.event_category}-${f.event_context}`}
                      label={formatEventName(f.event_name, f.event_context)}
                      value={f.total_count}
                      maxValue={topFeaturesMax}
                      color={getCategoryColor(f.event_category)}
                      subtitle={`${f.total_unique_users} usuários`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Feature Comparison */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <GitCompare className="h-5 w-5 text-gray-500" />
                Comparação por contexto
              </h2>
              <select
                value={comparisonEvent}
                onChange={(e) => setComparisonEvent(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 mb-4 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Selecionar evento para comparar"
              >
                <option value="">Selecione um evento para comparar...</option>
                {[...new Map(topFeatures.map((f) => [f.event_name, f])).values()].map((f) => (
                  <option key={f.event_name} value={f.event_name}>
                    {formatEventName(f.event_name)} ({f.total_count})
                  </option>
                ))}
              </select>
              {comparison.length === 0 ? (
                <div className="text-sm text-gray-500 py-8 text-center">
                  {comparisonEvent
                    ? 'Nenhuma divisão por contexto disponível para este evento'
                    : 'Selecione um evento acima para comparar o uso entre diferentes contextos da interface'}
                </div>
              ) : (
                <div className="space-y-4">
                  {comparison.map((c) => (
                    <HorizontalBar
                      key={c.event_context}
                      label={c.event_context.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())}
                      value={c.total_count}
                      maxValue={comparison[0]?.total_count || 1}
                      color="bg-primary-500"
                      subtitle={`${c.total_unique_users} usuários`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* By Tenant */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-gray-500" />
              Uso por estúdio
            </h2>
            {byTenant.length === 0 ? (
              <div className="text-sm text-gray-500 py-8 text-center">
                Nenhum dado por estúdio disponível
              </div>
            ) : (
              <div className="space-y-4">
                {byTenant.map((t) => (
                  <HorizontalBar
                    key={t.tenant_id}
                    label={t.tenant_name || t.tenant_slug}
                    value={t.total_events}
                    maxValue={byTenant[0]?.total_events || 1}
                    color="bg-indigo-500"
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
